import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
    BigModal,
    CheckboxInput,
    FlexDiv,
    TextLead,
    TextTiny,
    ThemedButton,
} from '@awarego-ui/components';
import { useStores } from '../../../../hooks/use-stores';
import AssessmentReminders from '../../../../components/manager/assessment/reminders';
import SelectChannel from '../../../../components/manager/wizard/select-channel';
import ErrorMessage from '../../../../components/errormessage';
import { TRANSPORTS } from '../../../../utils/constants';
import {
    defaultNextOccurrence,
    expandAssessmentReminderRrule,
    FIXED_STEPS_CAP,
    looksLikeWeeklySteps,
    weekdayCodeFromDate,
    buildRruleString,
    toTimeKey,
    normalizeExcludedDates,
} from '../../../../utils/assessmentRemindersRrule';
import RecurringRemindersBuilder from './RecurringRemindersBuilder';
import RemindersEventPreview from './RemindersEventPreview';

function splitSteps(steps = []) {
    const sent = [];
    const future = [];
    steps.forEach((step) => {
        if (step.status > 0) sent.push({ ...step });
        else future.push({ ...step });
    });
    return { sent, future };
}

function toMinuteKey(value) {
    const key = toTimeKey(value);
    return key == null ? null : Math.floor(key / 60);
}

function paramsFingerprint(mode, rrule, dtstart, futureSteps, due, sentDates) {
    if (mode === 'recurring') {
        const dates = expandAssessmentReminderRrule({
            rrule,
            dtstart,
            due,
            sentDates,
        });
        return `recurring|${dates.map(toTimeKey).join(',')}`;
    }
    const steps = (futureSteps || [])
        .map((s) => toTimeKey(s.send))
        .filter((key) => key != null)
        .join(',');
    return `fixed|${steps}`;
}

function EditRemindersModal({ isOpen, onClose, companyId }) {
    const { assessmentStore, authStore, store, companyStore } = useStores();
    const { editingAssessment, error, updatingRemindersSettings } =
        assessmentStore;
    const { timeZone } = authStore;

    const [enabled, setEnabled] = useState(false);
    const [mode, setMode] = useState('fixed');
    const [channel, setChannel] = useState(TRANSPORTS.EMAIL);
    const [futureSteps, setFutureSteps] = useState([]);
    const [sentSteps, setSentSteps] = useState([]);
    const [nextStepId, setNextStepId] = useState(1);
    const [rrule, setRrule] = useState(null);
    const [dtstart, setDtstart] = useState(null);
    const [rruleError, setRruleError] = useState(null);
    const [modeWarning, setModeWarning] = useState(null);
    const [builderKey, setBuilderKey] = useState(0);
    const [excluded, setExcluded] = useState([]);
    const paramsFingerprintRef = useRef(null);

    const slackConnected = Boolean(
        companyStore.slackDataIndex[companyId]?.connected
    );
    const teamsConnected = Boolean(
        companyStore.teamsDataIndex[companyId]?.connected &&
            companyStore.teamsDataIndex[companyId]?.enabled === '1'
    );

    useEffect(() => {
        if (!isOpen || !editingAssessment) return;
        const { sent, future } = splitSteps(
            editingAssessment.reminders_steps || []
        );
        setEnabled(Boolean(editingAssessment.reminders_enabled));
        setMode(editingAssessment.remindersMode || 'fixed');
        setChannel(editingAssessment.reminders_channel || TRANSPORTS.EMAIL);
        setSentSteps(sent);
        setFutureSteps(
            future.length > 0
                ? future.map((s) => ({ ...s }))
                : [{ id: 1, send: null }]
        );
        setNextStepId(
            Math.max(
                1,
                ...(editingAssessment.reminders_steps || []).map(
                    (s) => Number(s.id) || 0
                )
            ) + 1
        );
        setRrule(editingAssessment.remindersRrule || null);
        setDtstart(editingAssessment.remindersDtstart || null);
        setExcluded(
            normalizeExcludedDates(editingAssessment.remindersExcluded || [])
        );
        paramsFingerprintRef.current = paramsFingerprint(
            editingAssessment.remindersMode || 'fixed',
            editingAssessment.remindersRrule || null,
            editingAssessment.remindersDtstart || null,
            future,
            editingAssessment.due,
            sent.map((s) => s.send)
        );
        setRruleError(null);
        setModeWarning(null);
        setBuilderKey((k) => k + 1);
        assessmentStore.setError(null, 'saveRemindersSettings');
    }, [isOpen, editingAssessment, assessmentStore]);

    const sentDates = sentSteps.map((s) => s.send).filter(Boolean);
    const futureDates = futureSteps.map((s) => s.send).filter(Boolean);
    const excludedKeys = useMemo(
        () => new Set(excluded.map(toTimeKey).filter((key) => key != null)),
        [excluded]
    );
    const excludedMinuteKeys = useMemo(
        () => new Set(excluded.map(toMinuteKey).filter((key) => key != null)),
        [excluded]
    );

    const previewEvents = useMemo(() => {
        const locked = sentSteps.map((s) => ({
            send: s.send,
            status: s.status || 1,
            skipped: false,
        }));
        if (!enabled) {
            return locked.sort(
                (a, b) => new Date(a.send) - new Date(b.send)
            );
        }
        let draft = [];
        if (mode === 'recurring') {
            draft = expandAssessmentReminderRrule({
                rrule,
                dtstart,
                due: editingAssessment?.due,
                sentDates,
            }).map((d) => ({
                send: d,
                status: 0,
                skipped:
                    excludedKeys.has(toTimeKey(d)) ||
                    excludedMinuteKeys.has(toMinuteKey(d)),
            }));
        } else {
            const fromSteps = futureSteps
                .filter((s) => s.send)
                .map((s) => ({
                    send: s.send,
                    status: 0,
                    skipped:
                        excludedKeys.has(toTimeKey(s.send)) ||
                        excludedMinuteKeys.has(toMinuteKey(s.send)),
                }));
            const stepKeys = new Set(
                fromSteps.map((s) => toTimeKey(s.send)).filter((k) => k != null)
            );
            const extraSkipped = excluded
                .filter(
                    (d) =>
                        !stepKeys.has(toTimeKey(d)) &&
                        !fromSteps.some(
                            (s) => toMinuteKey(s.send) === toMinuteKey(d)
                        )
                )
                .map((d) => ({ send: d, status: 0, skipped: true }));
            draft = [...fromSteps, ...extraSkipped];
        }
        return [...locked, ...draft].sort(
            (a, b) => new Date(a.send) - new Date(b.send)
        );
    }, [
        enabled,
        editingAssessment?.due,
        excludedKeys,
        excludedMinuteKeys,
        futureSteps,
        mode,
        rrule,
        dtstart,
        sentDates,
        sentSteps,
        excluded,
    ]);

    const toggleExcluded = (send) => {
        const key = toTimeKey(send);
        if (key == null) return;
        const minuteKey = toMinuteKey(send);
        const iso = new Date(key * 1000).toISOString();
        const isIncluding =
            excludedKeys.has(key) || excludedMinuteKeys.has(minuteKey);
        setExcluded((current) => {
            if (isIncluding) {
                return current.filter(
                    (d) =>
                        toTimeKey(d) !== key && toMinuteKey(d) !== minuteKey
                );
            }
            return normalizeExcludedDates([...current, iso]);
        });
        if (mode === 'fixed' && isIncluding) {
            setFutureSteps((steps) => {
                if (steps.some((s) => toTimeKey(s.send) === key)) return steps;
                if (steps.filter((s) => s.send).length >= FIXED_STEPS_CAP) {
                    return steps;
                }
                return [...steps, { id: nextStepId, send: moment(iso) }];
            });
            setNextStepId((id) => id + 1);
        }
    };

    const clearSkippedIfParamsChanged = (nextFingerprint) => {
        if (
            paramsFingerprintRef.current != null &&
            paramsFingerprintRef.current !== nextFingerprint
        ) {
            setExcluded([]);
        }
        paramsFingerprintRef.current = nextFingerprint;
    };

    const switchMode = (nextMode) => {
        if (!nextMode || nextMode === mode) return;
        setModeWarning(null);
        if (nextMode === 'recurring') {
            const weekly = looksLikeWeeklySteps(futureDates);
            const start = defaultNextOccurrence({
                deliveryDate: editingAssessment.date,
                dueDate: editingAssessment.due,
                futureDates,
                sentDates,
            });
            const nextRrule = buildRruleString({
                freq: 'WEEKLY',
                interval: 1,
                byweekday: [weekdayCodeFromDate(start) || 'MO'],
            });
            const nextDtstart = start.toISOString();
            setRrule(nextRrule);
            setDtstart(nextDtstart);
            setBuilderKey((k) => k + 1);
            paramsFingerprintRef.current = paramsFingerprint(
                'recurring',
                nextRrule,
                nextDtstart,
                futureSteps,
                editingAssessment.due,
                sentDates
            );
            if (!weekly && futureDates.length > 0) {
                setModeWarning(
                    'Existing dates are not a weekly pattern. Recurring is set to weekly until due.'
                );
            }
        } else {
            const dates = expandAssessmentReminderRrule({
                rrule,
                dtstart,
                due: editingAssessment.due,
                sentDates,
            });
            let used = dates;
            if (dates.length > FIXED_STEPS_CAP) {
                used = dates.slice(0, FIXED_STEPS_CAP);
                setModeWarning(
                    `Recurring produced more than ${FIXED_STEPS_CAP} dates. Only the first ${FIXED_STEPS_CAP} will be used as fixed steps.`
                );
            }
            if (used.length === 0) {
                setFutureSteps([{ id: nextStepId, send: null }]);
                setNextStepId((id) => id + 1);
                paramsFingerprintRef.current = paramsFingerprint(
                    'fixed',
                    null,
                    null,
                    [{ id: nextStepId, send: null }],
                    editingAssessment.due,
                    sentDates
                );
            } else {
                const nextSteps = used.map((d, i) => ({
                    id: nextStepId + i,
                    send: moment(d),
                }));
                setFutureSteps(nextSteps);
                setNextStepId((id) => id + used.length);
                paramsFingerprintRef.current = paramsFingerprint(
                    'fixed',
                    null,
                    null,
                    nextSteps,
                    editingAssessment.due,
                    sentDates
                );
            }
        }
        setMode(nextMode);
        setExcluded([]);
    };

    const onChangeReminder = (date, i, reminder) => {
        setFutureSteps((steps) => {
            const next = steps.map((s, idx) =>
                idx === i || s.id === reminder.id ? { ...s, send: date } : s
            );
            clearSkippedIfParamsChanged(
                paramsFingerprint(
                    'fixed',
                    rrule,
                    dtstart,
                    next,
                    editingAssessment.due,
                    sentDates
                )
            );
            return next;
        });
    };

    const onAddReminderStep = () => {
        if (futureSteps.length >= FIXED_STEPS_CAP) return;
        const last = futureSteps[futureSteps.length - 1];
        const nextDate = last?.send ? moment(last.send).add(1, 'day') : null;
        const inRange =
            nextDate &&
            editingAssessment.due &&
            nextDate.isBefore(moment(editingAssessment.due));
        const next = [
            ...futureSteps,
            { id: nextStepId, send: inRange ? nextDate : null },
        ];
        setFutureSteps(next);
        setNextStepId((id) => id + 1);
        clearSkippedIfParamsChanged(
            paramsFingerprint(
                'fixed',
                rrule,
                dtstart,
                next,
                editingAssessment.due,
                sentDates
            )
        );
    };

    const onDeleteReminderStep = (reminder) => {
        const next = futureSteps.filter((s) => s.id !== reminder.id);
        setFutureSteps(next);
        clearSkippedIfParamsChanged(
            paramsFingerprint(
                'fixed',
                rrule,
                dtstart,
                next,
                editingAssessment.due,
                sentDates
            )
        );
    };

    const onChangeChannel = (event) => {
        const value = Number(event.target.value);
        const checked = event.target.checked;
        setChannel((current) => {
            let next = current;
            if (checked) next |= value;
            else next &= ~value;
            return next === 0 ? TRANSPORTS.EMAIL : next;
        });
    };

    const onSave = async () => {
        const body = {
            enabled,
            channel,
            mode,
        };
        if (enabled && mode === 'recurring') {
            body.rrule = rrule;
            body.dtstart = dtstart;
        }
        if (enabled && mode === 'fixed') {
            body.steps = futureSteps
                .filter((s) => s.send)
                .map((s) => ({
                    send: moment(s.send).toISOString(),
                }));
        }
        if (enabled) {
            body.excluded = excluded;
        }
        const result = await assessmentStore.saveRemindersSettings(
            companyId,
            editingAssessment.id,
            body
        );
        if (result) onClose();
    };

    if (!editingAssessment) return null;

    return (
        <BigModal
            isOpen={isOpen}
            onRequestClose={onClose}
            heading="Edit reminders"
            width={760}
            modalContent={
                <FlexDiv column gap={24}>
                    <ErrorMessage error={error} />
                    <CheckboxInput
                        checked={enabled}
                        onChange={() => setEnabled((v) => !v)}
                        name="sendReminders"
                        label={<TextLead bold>Send reminders</TextLead>}
                        noYPadding
                    />
                    {enabled && (
                        <Fragment>
                            <ToggleButtonGroup
                                exclusive
                                value={mode}
                                onChange={(_, value) => switchMode(value)}
                                size="small"
                            >
                                <ToggleButton value="fixed">
                                    Fixed steps
                                </ToggleButton>
                                <ToggleButton value="recurring">
                                    Recurring (beta)
                                </ToggleButton>
                            </ToggleButtonGroup>
                            {modeWarning && (
                                <TextTiny lighter>{modeWarning}</TextTiny>
                            )}
                            <SelectChannel
                                channel={channel}
                                slackAvailable={store.slackAvailable}
                                teamsAvailable={store.teamsAvailable}
                                slackConnected={slackConnected}
                                teamsConnected={teamsConnected}
                                onChangeChannel={onChangeChannel}
                                isReminders
                                removeTopMargin
                            />
                            {mode === 'fixed' ? (
                                <AssessmentReminders
                                    scheduledDate={
                                        editingAssessment.date || moment()
                                    }
                                    dueDate={editingAssessment.due}
                                    reminders_enabled={true}
                                    onChangeRemindersEnabled={() => {}}
                                    reminders_steps={futureSteps}
                                    onChangeReminder={onChangeReminder}
                                    onAddReminderStep={onAddReminderStep}
                                    onDeleteReminderStep={onDeleteReminderStep}
                                    channel={channel}
                                    slackAvailable={store.slackAvailable}
                                    teamsAvailable={store.teamsAvailable}
                                    slackConnected={slackConnected}
                                    teamsConnected={teamsConnected}
                                    onChangeChannel={onChangeChannel}
                                    adminTimeZone={timeZone}
                                    hideEnableCheckbox
                                    hideChannel
                                />
                            ) : (
                                <RecurringRemindersBuilder
                                    key={builderKey}
                                    initialRrule={rrule}
                                    initialDtstart={dtstart}
                                    deliveryDate={editingAssessment.date}
                                    dueDate={editingAssessment.due}
                                    futureDates={futureDates}
                                    sentDates={sentDates}
                                    timeZone={timeZone}
                                    onChange={({
                                        rrule: nextRule,
                                        dtstart: nextStart,
                                        error: nextError,
                                    }) => {
                                        setRrule(nextRule);
                                        setDtstart(nextStart);
                                        setRruleError(nextError);
                                        clearSkippedIfParamsChanged(
                                            paramsFingerprint(
                                                'recurring',
                                                nextRule,
                                                nextStart,
                                                futureSteps,
                                                editingAssessment.due,
                                                sentDates
                                            )
                                        );
                                    }}
                                />
                            )}
                        </Fragment>
                    )}
                    <RemindersEventPreview
                        events={previewEvents}
                        timeZone={timeZone}
                        canToggle={enabled}
                        onToggleExclude={toggleExcluded}
                    />
                </FlexDiv>
            }
            modalFooterContent={
                <Fragment>
                    <ThemedButton
                        mode="primary"
                        text={updatingRemindersSettings ? 'Saving' : 'Save'}
                        $saving={Boolean(updatingRemindersSettings)}
                        disabled={
                            Boolean(updatingRemindersSettings) ||
                            (enabled && mode === 'recurring' && Boolean(rruleError))
                        }
                        onClick={onSave}
                    />
                    <ThemedButton
                        mode="secondary"
                        onClick={onClose}
                        text="Cancel"
                    />
                </Fragment>
            }
        />
    );
}

export default observer(EditRemindersModal);
